import axios from 'axios';
import { useState, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem, CommandGroup, CommandSeparator } from '@/components/ui/command';
import { ChevronsUpDown, Check, Star } from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const uniteLabel = (u) => ({ pinte: 'Pinte', gallon: 'Gal.', chaudiere: 'Chaud.' }[u] || u || '');

// Cache global des favoris (partagé entre toutes les instances)
let favoritesCache = null;
let favoritesFetched = false;

async function loadFavorites() {
  if (favoritesFetched) return favoritesCache;
  try {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const res = await axios.get(`${API_URL}/api/produits/favorites`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.status === 200) {
      const data = res.data;
      favoritesCache = data.favorites || [];
    }
  } catch { /* silencieux */ }
  favoritesFetched = true;
  return favoritesCache || [];
}

function trackUsage(produitId) {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  axios.post(`${API_URL}/api/produits/${produitId}/track-usage`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }).catch(() => {});
}

// Invalider le cache après un track pour qu'il se recharge au prochain mount
function invalidateCache() {
  favoritesFetched = false;
  favoritesCache = null;
}

export function ProductSearchCombobox({ produits, value, onSelect, disabled, placeholder = "Rechercher un produit...", testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [favoriteIds, setFavoriteIds] = useState([]);
  const triggerRef = useRef(null);
  const [triggerWidth, setTriggerWidth] = useState(0);

  useEffect(() => {
    loadFavorites().then(ids => setFavoriteIds(ids || []));
  }, []);

  useEffect(() => {
    if (triggerRef.current) setTriggerWidth(triggerRef.current.offsetWidth);
  }, [open]);

  const selected = produits.find(p => p.id === value);
  const actifs = produits.filter(p => p.actif);

  const matchesSearch = (p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const searchStr = `${p.nom} ${p.code_produit || ''} ${(p.finis_disponibles || []).join(' ')} ${uniteLabel(p.unite)}`.toLowerCase();
    return searchStr.includes(q);
  };

  const filtered = actifs.filter(matchesSearch);

  // Séparer favoris et non-favoris
  const favSet = new Set(favoriteIds);
  const favoris = filtered.filter(p => favSet.has(p.id));
  const autres = filtered.filter(p => !favSet.has(p.id));
  const hasFavoris = favoris.length > 0 && !search.trim();

  const handleSelect = (prod) => {
    trackUsage(prod.id);
    invalidateCache();
    onSelect(prod);
    setOpen(false);
    setSearch('');
  };

  const renderItem = (p, isFav = false) => {
    const finis = (p.finis_disponibles || []).join(', ');
    return (
      <CommandItem
        key={p.id}
        value={p.id}
        onSelect={() => handleSelect(p)}
        className="flex items-center gap-2 py-2"
      >
        <Check className={cn("h-3.5 w-3.5 shrink-0", value === p.id ? "opacity-100" : "opacity-0")} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {isFav && <Star className="h-3 w-3 text-amber-400 fill-amber-400 shrink-0" />}
            <span className="font-medium text-sm">{p.nom}</span>
          </div>
          {finis && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
              <span className="text-violet-600">{finis}</span>
              <span className="text-blue-600">{uniteLabel(p.unite)}</span>
            </div>
          )}
        </div>
      </CommandItem>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            disabled && "opacity-50 cursor-not-allowed",
            !selected && "text-muted-foreground"
          )}
          data-testid={testId}
        >
          <span className="truncate text-left">
            {selected ? selected.nom : placeholder}
          </span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start" style={{ width: Math.max(triggerWidth, 320) }}>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Taper pour rechercher..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>Aucun produit trouvé</CommandEmpty>
            {hasFavoris && (
              <>
                <CommandGroup heading="Fréquemment utilisés">
                  {favoris.map(p => renderItem(p, true))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup heading={hasFavoris ? "Tous les produits" : undefined}>
              {(hasFavoris ? autres : filtered).slice(0, 50).map(p => renderItem(p))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
